import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import type { ManualHealResponse } from '@/types/bot-account-heal';

export function BotAccountErrorsPanel() {
  const { toast } = useToast();
  const [selectedErrors, setSelectedErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>('');

  const handleRunAutoHeal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('auto-heal-processor');
      if (error) throw error;
      toast({
        title: "Auto-Heal Triggered",
        description: "Processing pending healing tasks..."
      });
      setTimeout(() => refetch(), 2000);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to trigger auto-heal",
        variant: "destructive"
      });
    }
  };

  const handleCleanupStuck = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('cleanup-stuck-tasks');
      if (error) throw error;
      toast({
        title: "Cleanup Triggered",
        description: data?.message || "Stuck tasks cleaned up successfully"
      });
      setTimeout(() => refetch(), 2000);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to cleanup stuck tasks",
        variant: "destructive"
      });
    }
  };

  const { data: errors, isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['bot-account-errors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bot_account_errors')
        .select(`
          *,
          bot_accounts(
            id,
            user_id,
            bot_id,
            accountid_trading,
            status,
            bots!fk_bot_id(name)
          )
        `)
        .order('last_error_at', { ascending: false, nullsFirst: false });
      
      if (error) {
        console.error('❌ Query error:', error);
        throw error;
      }
      return data;
    },
    refetchInterval: 30000,
    retry: 2
  });

  const handleSelectError = (botAccountId: string, checked: boolean) => {
    if (checked) {
      setSelectedErrors(prev => [...prev, botAccountId]);
    } else {
      setSelectedErrors(prev => prev.filter(id => id !== botAccountId));
    }
  };

  const handleSelectAll = () => {
    setSelectedErrors(activeErrors.map(e => e.bot_account_id));
  };

  const handleDeselectAll = () => {
    setSelectedErrors([]);
  };

  const handleResetAttempts = async () => {
    if (selectedErrors.length === 0) return;

    setIsProcessing(true);
    setProcessingStatus(`Resetting auto-heal attempts for ${selectedErrors.length} account(s)...`);

    try {
      const { data, error } = await supabase.functions.invoke('bot-account-manual-heal', {
        body: {
          bot_account_ids: selectedErrors,
          action: 'reset_attempts'
        }
      });

      if (error) throw error;

      const result = data as ManualHealResponse;

      toast({
        title: "Reset Complete",
        description: `Successfully reset ${result.successful} account(s). ${result.failed} failed.`,
        variant: result.failed > 0 ? "destructive" : "default"
      });

      setSelectedErrors([]);
      setTimeout(() => refetch(), 1000);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to reset auto-heal attempts",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
    }
  };

  const handleBulkResolve = async () => {
    if (selectedErrors.length === 0) return;

    setIsProcessing(true);
    setProcessingStatus(`Resolving ${selectedErrors.length} account(s)...`);

    try {
      const { data, error } = await supabase.functions.invoke('bot-account-manual-heal', {
        body: {
          bot_account_ids: selectedErrors,
          action: 'resolve'
        }
      });

      if (error) throw error;

      const result = data as ManualHealResponse;

      toast({
        title: "Resolve Complete",
        description: `Successfully resolved ${result.successful} account(s). ${result.failed} failed.`,
        variant: result.failed > 0 ? "destructive" : "default"
      });

      setSelectedErrors([]);
      setTimeout(() => refetch(), 1000);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to resolve accounts",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
    }
  };

  const handleResolve = async (botAccountId: string) => {
    setSelectedErrors([botAccountId]);
    await handleBulkResolve();
  };

  const activeErrors = errors?.filter(e => !e.is_resolved && e.error_message) || [];
  const resolvedErrors = errors?.filter(e => e.is_resolved) || [];
  const allErrors = errors || [];

  if (isLoading) {
    return <div className="text-center py-8">Loading errors...</div>;
  }

  if (queryError) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-destructive">
            <AlertCircle className="w-12 h-12 mx-auto mb-4" />
            <p className="font-medium">Failed to load bot account errors</p>
            <p className="text-sm mt-2 text-muted-foreground">{queryError.message}</p>
            <Button onClick={() => refetch()} className="mt-4">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Active Errors</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-destructive">{activeErrors.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Resolved</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600 dark:text-green-500">{resolvedErrors.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Total Tracked</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{allErrors.length}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Bot Account Connection Errors</CardTitle>
            <div className="flex gap-2 flex-wrap">
              {activeErrors.length > 0 && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={isProcessing}
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleDeselectAll}
                    disabled={isProcessing || selectedErrors.length === 0}
                  >
                    Deselect All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleResetAttempts}
                    disabled={isProcessing || selectedErrors.length === 0}
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Reset Attempts ({selectedErrors.length})
                  </Button>
                  <Button 
                    size="sm"
                    onClick={handleBulkResolve}
                    disabled={isProcessing || selectedErrors.length === 0}
                  >
                    Resolve & Reconnect ({selectedErrors.length})
                  </Button>
                </>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleCleanupStuck}
                disabled={isProcessing}
              >
                Cleanup Stuck Tasks
              </Button>
              <Button 
                size="sm"
                onClick={handleRunAutoHeal}
                disabled={isProcessing}
              >
                Run Auto-Heal Now
              </Button>
            </div>
          </div>
          {isProcessing && (
            <div className="mt-2 text-sm text-muted-foreground">
              {processingStatus}
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {activeErrors.map((error) => (
              <div key={error.bot_account_id} className="flex items-start gap-4 p-4 border rounded-lg bg-card">
                <Checkbox
                  checked={selectedErrors.includes(error.bot_account_id)}
                  onCheckedChange={(checked) => handleSelectError(error.bot_account_id, checked as boolean)}
                  disabled={isProcessing}
                  className="mt-1"
                />
                <AlertCircle className="w-5 h-5 text-destructive mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-medium">
                      {error.bot_accounts?.bots?.name || error.bot_accounts?.bot_id || 'Deleted Bot'}
                    </span>
                    <Badge variant="secondary">
                      {error.bot_accounts?.accountid_trading || 'N/A'}
                    </Badge>
                    {error.error_code && <Badge variant="outline">{error.error_code}</Badge>}
                    <Badge variant="outline" className="text-xs">
                      Heal: {error.auto_heal_attempts || 0}/50
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2 break-words">{error.error_message}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {error.last_error_at && formatDistanceToNow(new Date(error.last_error_at), { addSuffix: true })}
                    </span>
                    <span>Occurrences: {error.error_count}</span>
                  </div>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => handleResolve(error.bot_account_id)}
                  className="flex-shrink-0"
                  disabled={isProcessing}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Resolve
                </Button>
              </div>
            ))}
            {activeErrors.length === 0 && (
              <div className="text-center text-muted-foreground py-8 flex flex-col items-center gap-2">
                <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-500" />
                <p className="font-medium">No active errors</p>
                <p className="text-sm">All bot account connections are working properly</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
